import { ReactNode } from 'react'
import clsx from 'clsx'

interface CardProps {
  children: ReactNode
  className?: string
  hover?: boolean
  padding?: 'none' | 'sm' | 'md' | 'lg'
  /** `panel` uses the larger 28px radius — for primary surfaces like an auth form. */
  radius?: 'card' | 'panel'
}

/**
 * Brand-styled card component.
 *
 * Flat surface separated by a hairline border — depth is reserved for
 * overlays (menus, dialogs), not for every panel on the page.
 */
export function Card({
  children,
  className,
  hover = false,
  padding = 'md',
  radius = 'card',
}: CardProps) {
  const paddings = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  }

  return (
    <div
      className={clsx(
        'bg-surface border border-hairline',
        radius === 'panel' ? 'rounded-panel' : 'rounded-card',
        hover && 'transition-colors duration-fast ease-standard hover:bg-surface-alt/40',
        paddings[padding],
        className
      )}
    >
      {children}
    </div>
  )
}

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ElementType
  /** Brand semantic colors - NO RED for financial data! */
  color: 'teal' | 'clay' | 'mint' | 'info' | 'warning'
  subValue?: string
  trend?: {
    value: number
    label: string
    isPositive: boolean
  }
}

/**
 * Dashboard stat card following brand guidelines
 *
 * Color usage:
 * - teal: Primary metrics, in-progress states
 * - clay: Warm accent, Amani-related
 * - mint: Success states, positive metrics
 * - info: Informational metrics
 * - warning: Action needed (NOT overdue!) - uses warm orange
 *
 * IMPORTANT: Per brand guidelines, we never use red for financial data
 * to avoid shame/negative associations
 */
export function StatCard({
  title,
  value,
  icon: Icon,
  color,
  subValue,
  trend,
}: StatCardProps) {
  const colorClasses = {
    teal: 'bg-accent-soft text-accent',
    clay: 'bg-highlight-soft text-highlight',
    mint: 'bg-success-soft text-success',
    info: 'bg-surface-alt text-ink-muted',
    warning: 'bg-warning-soft text-warning',
  }

  return (
    <Card className="relative overflow-hidden">
      <div className="flex items-start gap-4">
        <div className={clsx('p-3 rounded-chip', colorClasses[color])}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-inter text-body-sm text-ink-muted">{title}</p>
          <p className="font-outfit text-[clamp(1.5rem,2.3vw,2rem)] text-ink break-words tabular">{value}</p>
          {subValue && (
            <p className="font-inter text-caption text-ink-faint mt-1">{subValue}</p>
          )}
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <span
                className={clsx(
                  'font-inter text-caption font-medium',
                  trend.isPositive ? 'text-success' : 'text-warning'
                )}
              >
                {trend.isPositive ? '+' : ''}{trend.value}%
              </span>
              <span className="font-inter text-caption text-ink-faint">
                {trend.label}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

export interface MetricCardProps {
  title: string
  value: string | number
  subtitle?: string
  progress?: number
  color?: 'teal' | 'mint' | 'clay' | 'info'
  icon?: React.ElementType
  children?: ReactNode
}

/**
 * Metric card with optional progress bar
 * Following the "progress over problems" philosophy
 */
export function MetricCard({
  title,
  value,
  subtitle,
  progress,
  color = 'teal',
  icon: Icon,
  children,
}: MetricCardProps) {
  const progressColors: Record<string, string> = {
    teal: 'from-teal-500 to-mint-400',
    mint: 'from-mint-400 to-mint-300',
    clay: 'from-clay-500 to-clay-400',
    info: 'from-info-500 to-info-400',
  }

  return (
    <Card>
      <div className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-5 w-5 text-ink-faint" />}
            <div>
              <h3 className="font-outfit text-h4 text-ink">{title}</h3>
              {subtitle && (
                <p className="font-inter text-body-sm text-ink-muted mt-0.5">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <span className="font-outfit text-metric text-ink break-words tabular">{value}</span>
        </div>

        {progress !== undefined && (
          <div className="space-y-1">
            <div className="h-2 bg-surface-alt rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full rounded-full bg-gradient-to-r transition-all duration-500',
                  progressColors[color]
                )}
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
            <p className="font-inter text-caption text-ink-faint text-right">
              {progress.toFixed(0)}% collected
            </p>
          </div>
        )}

        {children}
      </div>
    </Card>
  )
}
